import z from "zod";

import { type ErrorDetails, RemiHttpCode } from "./remi-app";

const amfamIntegrationErrorTypeValues = [
  "TEST_ERROR",
  "MISSING_INNGEST_SIGNING_KEY",
  "VERISK_API_ERROR",
  "MISSING_WORKSITE",
  "INGEST_MALFORMED_FNOL_PAYLOAD",
  "INGEST_MISSING_EVENT_ID",
  "CREATE_BID_MALFORMED_EVENT_PAYLOAD",
  "PRISMA_MODEL_SURFACE_DRIFT",
  "MISSING_REMI_AUTOMATIONS_XA_AUTHOR",
  "MISSING_XACTANALYSIS_USER_ID",
  "INGEST_NOTE_AMBIGUOUS_DATASET",
  "INGEST_NOTE_READY_WITHOUT_ASSIGNMENT",
  "XACTANALYSIS_TOKEN_MINT_FAILED",
  "WEBHOOK_SIGNATURE_INVALID",
  "REMI_WEBHOOK_INVALID_JSON",
  "REMI_WEBHOOK_MALFORMED_ENVELOPE",
  "REMI_WEBHOOK_MALFORMED_PAYLOAD",
  "PROJECT_CATEGORY_UNKNOWN",
  "AMFAM_POST_STATUS_NON_ERROR_THROWN",
  "INNGEST_SYNC_FAILED",
  "MISSING_FC_URL",
  "INGEST_ESTIMATE_MISSING_EVENT_ID",
  "FORWARD_ESTIMATE_ASSIGNMENT_NOT_FOUND",
  "FORWARD_ESTIMATE_ASSIGNMENT_EXTERNAL_ID_MISSING",
  "FORWARD_ESTIMATE_BID_NOT_CREATED",
  "INGEST_ESTIMATE_BID_REQUEST_UNBUILDABLE",
  "INGEST_CLIENT_APPROVED_MISSING_EVENT_ID",
  "INGEST_CLIENT_APPROVED_AMBIGUOUS_DATASET",
  "INGEST_CLIENT_REJECTED_MISSING_EVENT_ID",
  "INGEST_CLIENT_REJECTED_AMBIGUOUS_DATASET",
  "PUSH_FILE_REPORT_AMBIGUOUS_DATASET",
] as const;
const amfamIntegrationErrorTypeSchema = z.enum(amfamIntegrationErrorTypeValues);
const AmfamIntegrationErrorType = amfamIntegrationErrorTypeSchema.enum;
type AmfamIntegrationErrorType = z.infer<typeof amfamIntegrationErrorTypeSchema>;

interface ParseIssueSummary {
  path: PropertyKey[];
  code: string;
}

export type AmfamIntegrationErrors =
  | {
      errorType: typeof AmfamIntegrationErrorType.TEST_ERROR;
      data: undefined;
    }
  | {
      errorType: typeof AmfamIntegrationErrorType.MISSING_INNGEST_SIGNING_KEY;
      data: undefined;
    }
  | {
      errorType: typeof AmfamIntegrationErrorType.VERISK_API_ERROR;
      data: { statusCode: number; endpoint: string };
    }
  | {
      errorType: typeof AmfamIntegrationErrorType.MISSING_WORKSITE;
      data: { xaAssignmentId: string; dataset: string };
    }
  | {
      errorType: typeof AmfamIntegrationErrorType.INGEST_MALFORMED_FNOL_PAYLOAD;
      data: { eventName: string; eventId: string | undefined; eventTs: number | undefined };
    }
  | {
      errorType: typeof AmfamIntegrationErrorType.INGEST_MISSING_EVENT_ID;
      data: { eventName: string; eventTs: number | undefined };
    }
  | {
      errorType: typeof AmfamIntegrationErrorType.CREATE_BID_MALFORMED_EVENT_PAYLOAD;
      data: { eventName: string; eventId: string | undefined; eventTs: number | undefined };
    }
  | {
      errorType: typeof AmfamIntegrationErrorType.PRISMA_MODEL_SURFACE_DRIFT;
      data: { unexpected: string[] };
    }
  | {
      errorType: typeof AmfamIntegrationErrorType.MISSING_REMI_AUTOMATIONS_XA_AUTHOR;
      data: { xaAssignmentId: string | null };
    }
  | {
      errorType: typeof AmfamIntegrationErrorType.MISSING_XACTANALYSIS_USER_ID;
      data: undefined;
    }
  | {
      errorType: typeof AmfamIntegrationErrorType.INGEST_NOTE_AMBIGUOUS_DATASET;
      data: { xaAssignmentId: string; claimNumber: string; matchedDatasets: string[] };
    }
  | {
      errorType: typeof AmfamIntegrationErrorType.INGEST_NOTE_READY_WITHOUT_ASSIGNMENT;
      data: { functionId: string; runId: string; eventName: string };
    }
  | {
      errorType: typeof AmfamIntegrationErrorType.XACTANALYSIS_TOKEN_MINT_FAILED;
      data: { httpStatusCode: number | undefined; message: string };
    }
  | {
      errorType: typeof AmfamIntegrationErrorType.WEBHOOK_SIGNATURE_INVALID;
      data: { svixId: string };
    }
  | {
      errorType: typeof AmfamIntegrationErrorType.REMI_WEBHOOK_INVALID_JSON;
      data: { svixId: string };
    }
  | {
      errorType: typeof AmfamIntegrationErrorType.REMI_WEBHOOK_MALFORMED_ENVELOPE;
      data: { svixId: string; parseIssues: ParseIssueSummary[] };
    }
  | {
      errorType: typeof AmfamIntegrationErrorType.REMI_WEBHOOK_MALFORMED_PAYLOAD;
      data: { svixId: string; eventType: string; parseIssues: ParseIssueSummary[] };
    }
  | {
      errorType: typeof AmfamIntegrationErrorType.PROJECT_CATEGORY_UNKNOWN;
      data: { projectCategory: string | null; bidId: number };
    }
  | {
      errorType: typeof AmfamIntegrationErrorType.AMFAM_POST_STATUS_NON_ERROR_THROWN;
      data: { svixId: string; originalValue: string };
    }
  | {
      errorType: typeof AmfamIntegrationErrorType.INNGEST_SYNC_FAILED;
      data: { httpStatusCode: number | undefined; message: string };
    }
  | {
      errorType: typeof AmfamIntegrationErrorType.MISSING_FC_URL;
      data: undefined;
    }
  | {
      errorType: typeof AmfamIntegrationErrorType.INGEST_ESTIMATE_MISSING_EVENT_ID;
      data: { eventName: string; eventTs: number | undefined };
    }
  | {
      errorType: typeof AmfamIntegrationErrorType.FORWARD_ESTIMATE_ASSIGNMENT_NOT_FOUND;
      data: { xactanalysisAssignmentId: string };
    }
  | {
      errorType: typeof AmfamIntegrationErrorType.FORWARD_ESTIMATE_ASSIGNMENT_EXTERNAL_ID_MISSING;
      data: { xactanalysisAssignmentId: string; xactanalysisEventId: string };
    }
  | {
      errorType: typeof AmfamIntegrationErrorType.FORWARD_ESTIMATE_BID_NOT_CREATED;
      data: { functionId: string; runId: string; eventName: string };
    }
  | {
      errorType: typeof AmfamIntegrationErrorType.INGEST_ESTIMATE_BID_REQUEST_UNBUILDABLE;
      data: { xactanalysisAssignmentId: string; xaAssignmentId: string; dataset: string };
    }
  | {
      errorType: typeof AmfamIntegrationErrorType.INGEST_CLIENT_APPROVED_MISSING_EVENT_ID;
      data: { eventName: string; eventTs: number | undefined };
    }
  | {
      errorType: typeof AmfamIntegrationErrorType.INGEST_CLIENT_APPROVED_AMBIGUOUS_DATASET;
      data: { xaAssignmentId: string; matchedDatasets: string[] };
    }
  | {
      errorType: typeof AmfamIntegrationErrorType.INGEST_CLIENT_REJECTED_MISSING_EVENT_ID;
      data: { eventName: string; eventTs: number | undefined };
    }
  | {
      errorType: typeof AmfamIntegrationErrorType.INGEST_CLIENT_REJECTED_AMBIGUOUS_DATASET;
      data: { xaAssignmentId: string; matchedDatasets: string[] };
    }
  | {
      errorType: typeof AmfamIntegrationErrorType.PUSH_FILE_REPORT_AMBIGUOUS_DATASET;
      data: { svixId: string; bidId: number; xaAssignmentId: string; matchedDatasets: string[] };
    };

export const amfamIntegrationErrorDetails: Record<AmfamIntegrationErrorType, ErrorDetails> = {
  [AmfamIntegrationErrorType.TEST_ERROR]: {
    sentryDescription: "Test error for verifying error handling",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [AmfamIntegrationErrorType.MISSING_INNGEST_SIGNING_KEY]: {
    sentryDescription: "AmFam server starting without INNGEST_SIGNING_KEY in production",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [AmfamIntegrationErrorType.VERISK_API_ERROR]: {
    sentryDescription: "Verisk XactAnalysis API call failed",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [AmfamIntegrationErrorType.MISSING_WORKSITE]: {
    sentryDescription: "AmFam FNOL digest missing worksite — cannot create insurance bid",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [AmfamIntegrationErrorType.INGEST_MALFORMED_FNOL_PAYLOAD]: {
    sentryDescription: "AmFam ingest received an FNOL event whose payload failed Zod validation",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [AmfamIntegrationErrorType.INGEST_MISSING_EVENT_ID]: {
    sentryDescription: "AmFam ingest received an Inngest event with no event.id",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [AmfamIntegrationErrorType.CREATE_BID_MALFORMED_EVENT_PAYLOAD]: {
    sentryDescription: "AmFam create-bid handler received an event whose payload failed Zod validation",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [AmfamIntegrationErrorType.PRISMA_MODEL_SURFACE_DRIFT]: {
    sentryDescription: "AmFam prisma client exposes unexpected models — globalThis cross-client collision suspected",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [AmfamIntegrationErrorType.MISSING_REMI_AUTOMATIONS_XA_AUTHOR]: {
    sentryDescription:
      "AmFam ingest-note ran without REMI_AUTOMATIONS_XA_AUTHOR set — round-trip echo defense disabled until Doppler is populated",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [AmfamIntegrationErrorType.MISSING_XACTANALYSIS_USER_ID]: {
    sentryDescription:
      "AmFam server booted without XACTANALYSIS_USER_ID set — outbound addNote would fall back to XactNet System and silently defeat the round-trip echo defense",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [AmfamIntegrationErrorType.INGEST_NOTE_AMBIGUOUS_DATASET]: {
    sentryDescription:
      "AmFam inbound XA note matched multiple xactanalysisAssignment rows across datasets — cannot disambiguate without a dataset signal in the note webhook",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [AmfamIntegrationErrorType.INGEST_NOTE_READY_WITHOUT_ASSIGNMENT]: {
    sentryDescription:
      "AmFam inbound XA note saw assignment.ready fire but the XactanalysisAssignment row stayed unresolvable through retry exhaustion — the ready signal and the assignment write have decoupled",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [AmfamIntegrationErrorType.XACTANALYSIS_TOKEN_MINT_FAILED]: {
    sentryDescription: "AmFam XactAnalysis OAuth token mint failed",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [AmfamIntegrationErrorType.WEBHOOK_SIGNATURE_INVALID]: {
    sentryDescription: "AmFam /webhooks/remi received a request whose Svix signature failed verification",
    remiHttpCode: RemiHttpCode.UNAUTHORIZED,
  },
  [AmfamIntegrationErrorType.REMI_WEBHOOK_INVALID_JSON]: {
    sentryDescription: "AmFam /webhooks/remi received a signed request whose body was not valid JSON",
    remiHttpCode: RemiHttpCode.BAD_REQUEST,
  },
  [AmfamIntegrationErrorType.REMI_WEBHOOK_MALFORMED_ENVELOPE]: {
    sentryDescription: "AmFam /webhooks/remi received a signed payload whose envelope failed Zod validation",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [AmfamIntegrationErrorType.REMI_WEBHOOK_MALFORMED_PAYLOAD]: {
    sentryDescription:
      "AmFam /webhooks/remi received a signed envelope whose event-specific payload failed Zod validation",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [AmfamIntegrationErrorType.PROJECT_CATEGORY_UNKNOWN]: {
    sentryDescription:
      "AmFam outbound status mapper received a PROJECT_SCHEDULED payload with an unknown projectCategory — falling back to non-site-visit fan-out",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [AmfamIntegrationErrorType.AMFAM_POST_STATUS_NON_ERROR_THROWN]: {
    sentryDescription:
      "AmFam post-status-to-xa caught a non-Error value thrown from inside the fan-out — typed wrapping fallback",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [AmfamIntegrationErrorType.INNGEST_SYNC_FAILED]: {
    sentryDescription:
      "AmFam server failed to self-sync its Inngest function registry on boot — Inngest will keep dispatching events against the previous deploy's function set until a manual sync runs",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [AmfamIntegrationErrorType.MISSING_FC_URL]: {
    sentryDescription:
      "AmFam server booted in production without FC_URL set — boot-time Inngest sync was skipped to avoid registering the loopback as the callback URL; existing Inngest registration still routes events, but the deploy's new/changed functions require a manual sync until FC_URL is configured",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [AmfamIntegrationErrorType.INGEST_ESTIMATE_MISSING_EVENT_ID]: {
    sentryDescription: "AmFam ingest received an estimate-package Inngest event with no event.id",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [AmfamIntegrationErrorType.FORWARD_ESTIMATE_ASSIGNMENT_NOT_FOUND]: {
    sentryDescription: "AmFam forward-estimate-to-remi could not find the XactanalysisAssignment row",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [AmfamIntegrationErrorType.FORWARD_ESTIMATE_ASSIGNMENT_EXTERNAL_ID_MISSING]: {
    sentryDescription:
      "AmFam forward-estimate-to-remi reached the collaboration-estimate ticket step with a null externalId, which the check-assignment-linked step should have already ruled out",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [AmfamIntegrationErrorType.FORWARD_ESTIMATE_BID_NOT_CREATED]: {
    sentryDescription:
      "AmFam forward-estimate-to-remi exhausted its retries waiting for a bid to be linked to the assignment, so the estimate was never forwarded",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [AmfamIntegrationErrorType.INGEST_ESTIMATE_BID_REQUEST_UNBUILDABLE]: {
    sentryDescription:
      "AmFam ingest-estimate received an estimate for an assignment with no linked bid, but the digest carried no assignment block to request one from",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [AmfamIntegrationErrorType.INGEST_CLIENT_APPROVED_MISSING_EVENT_ID]: {
    sentryDescription: "AmFam ingest received a client-approved Inngest event with no event.id",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [AmfamIntegrationErrorType.INGEST_CLIENT_APPROVED_AMBIGUOUS_DATASET]: {
    sentryDescription:
      "AmFam client-approved matched multiple xactanalysisAssignment rows across datasets — cannot disambiguate without a dataset signal in the client-approved webhook",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [AmfamIntegrationErrorType.INGEST_CLIENT_REJECTED_MISSING_EVENT_ID]: {
    sentryDescription: "AmFam ingest received a client-rejected Inngest event with no event.id",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [AmfamIntegrationErrorType.INGEST_CLIENT_REJECTED_AMBIGUOUS_DATASET]: {
    sentryDescription:
      "AmFam client-rejected matched multiple xactanalysisAssignment rows across datasets — cannot disambiguate without a dataset signal in the client-rejected webhook",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [AmfamIntegrationErrorType.PUSH_FILE_REPORT_AMBIGUOUS_DATASET]: {
    sentryDescription:
      "AmFam outbound file sync matched multiple linked xactanalysisAssignment rows across datasets for one xaAssignmentId — cannot disambiguate which BidRequest's report to render, so the report leg was skipped",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
};
